import {
  type NavigateOptions,
  useSearchParams as useSearchParamsReactRouter,
} from 'react-router-dom';

const defaultSearchParamsOptions: NavigateOptions = {
  replace: true,
};

export const useSearchParams = (filters: string[]) => {
  const [_searchParams, _setSearchParams] = useSearchParamsReactRouter();

  const getSearchParam = (key: string) => _searchParams.get(key);

  /**
   * Get all search params as an object.
   */
  const getSearchParams = () => {
    const params: Record<string, string> = {};
    filters.forEach(key => {
      params[key] = _searchParams.get(key) || '';
    });
    return params;
  };

  const changeSearchParam = (
    key: string,
    value: string | number,
    options?: NavigateOptions,
  ) => {
    _setSearchParams(
      currentSearchParams => {
        currentSearchParams.set(key, String(value));

        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  /**
   * Change multiple search params at once.
   *
   * @param values - Object with key-value pairs to update search params
   * @param options - Navigation options for setSearchParams
   */
  const changeSearchParams = (
    values: Record<string, string | number>,
    options?: NavigateOptions,
  ) => {
    _setSearchParams(
      currentSearchParams => {
        Object.entries(values).forEach(([key, value]) => {
          currentSearchParams.set(key, String(value));
        });
        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  /**
   * Set multiple search params at once.
   * When a filter is not present in the URL, it will be added.
   * When a filter is not provided in the values object, it will be removed.
   *
   * @param values - Object with key-value pairs to update search params.
   * @param options - Navigation options for setSearchParams.
   */
  const setSearchParams = (
    values: Record<string, string | number>,
    options?: NavigateOptions,
  ) => {
    _setSearchParams(
      currentSearchParams => {
        filters.forEach(filterMap => {
          if (currentSearchParams.has(filterMap)) {
            currentSearchParams.delete(filterMap);
          }
        });

        Object.entries(values).forEach(([key, value]) => {
          if (String(value) !== '') {
            currentSearchParams.set(key, String(value));
          }
        });
        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  const deleteSearchParam = (key: string, options?: NavigateOptions) => {
    _setSearchParams(
      currentSearchParams => {
        currentSearchParams.delete(key);

        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  const deleteSearchParams = (keys: string[], options?: NavigateOptions) => {
    _setSearchParams(
      currentSearchParams => {
        keys.forEach(key => {
          currentSearchParams.delete(key);
        });

        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  const deleteAllSearchParams = (options?: NavigateOptions) => {
    _setSearchParams(
      currentSearchParams => {
        filters.forEach(key => {
          currentSearchParams.delete(key);
        });

        return currentSearchParams;
      },
      {
        ...defaultSearchParamsOptions,
        ...options,
      },
    );
  };

  return {
    getSearchParam,
    getSearchParams,
    changeSearchParam,
    changeSearchParams,
    setSearchParams,
    deleteSearchParam,
    deleteSearchParams,
    deleteAllSearchParams,
  };
};
